import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useApiClient from "../../hooks/useApiClient";
import Dropdown from "../dropdowns/dropdown";
import { CloseButton } from "../closeButton";

const UserManagement = ({ organizationId }: { organizationId: string }) => {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { data: organizationUsers, isLoading: orgUsersLoading } = useQuery({
    queryKey: ["organizationUsers", organizationId],
    queryFn: () => api.users.listOrganizationUsers({ organizationId }),
  });

  const { data: allUsers, isLoading: allUsersLoading } = useQuery({
    queryKey: ["allUsers"],
    queryFn: () => api.users.listUsers(),
  });

  const removeUserMutation = useMutation({
    mutationFn: (userId: string) => api.users.removeUserFromOrganization({ userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizationUsers", organizationId] });
    },
  });

  const addUserMutation = useMutation({
    mutationFn: (userId: string) => api.users.addUserToOrganization({ userId, organizationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizationUsers", organizationId] });
      setSelectedUserId("");
    },
  });

  const handleRemoveUser = (userId: string) => {
    removeUserMutation.mutate(userId);
  };

  const handleAddUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedUserId) {
      addUserMutation.mutate(selectedUserId);
    }
  };

  if (orgUsersLoading || allUsersLoading) return <div>Loading users...</div>;

  const availableUsers = allUsers!.filter(
    (user) => !organizationUsers!.items.some((orgUser) => orgUser.id === user.id)
  );

  return (
    <div>
      <h2 className="mb-4 text-2xl font-semibold text-blue-200">Users</h2>
      <div className="mb-6">
        <h3 className="mb-2 text-lg font-semibold text-blue-200">
          Current Users
        </h3>
        {organizationUsers!.items.length === 0 ? (
          <p>No users in this organization.</p>
        ) : (
          <ul className="space-y-2">
            {organizationUsers!.items.map((user) => (
              <li key={user.id} className="flex items-center justify-between">
                <span>{user.email}</span>
                <CloseButton handleClick={() => handleRemoveUser(user.id)} className="" />
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <h3 className="mb-2 text-lg font-semibold text-blue-200">
          Add New User
        </h3>
        <form onSubmit={handleAddUser} className="flex items-center">
          <div className="mr-2 w-64">
            <Dropdown
              label="Select a user"
              isOpen={isDropdownOpen}
              setIsOpen={setIsDropdownOpen}
            >
              <div className="py-1">
                {availableUsers.map((user) => (
                  <button
                    key={user.id}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    onClick={() => {
                      setSelectedUserId(user.id);
                      setIsDropdownOpen(false);
                    }}
                  >
                    {user.email}
                  </button>
                ))}
              </div>
            </Dropdown>
          </div>
        </form>
      </div>
      {addUserMutation.isPending && (
        <p className="mt-2 text-blue-500">Adding user...</p>
      )}
      {addUserMutation.isError && (
        <p className="mt-2 text-red-500">
          Error adding user: {addUserMutation.error.message}
        </p>
      )}
      {removeUserMutation.isPending && (
        <p className="mt-2 text-blue-500">Removing user...</p>
      )}
      {removeUserMutation.isError && (
        <p className="mt-2 text-red-500">
          Error removing user: {removeUserMutation.error.message}
        </p>
      )}
    </div>
  );
};

export default UserManagement;
